import React, { useState, useMemo } from "react"
import {
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  CardHeader,
  CardFooter,
  Alert
} from "reactstrap"
import moment from "moment"
import Select from "react-select"
import { FilePond, registerPlugin } from "react-filepond"
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation"
import FilePondPluginImagePreview from "filepond-plugin-image-preview"
import Datetime from "react-datetime"
import _, { flowRight as compose } from "lodash"
import { useMutation, useQuery } from "@apollo/react-hooks"
import { withRouter } from "react-router"
import { withBasicNotify } from "components/notification"
import { Img } from "components/image"
import { BarLoader } from "components/spinner"
import { BackButton } from "components/utility"
import { announcementTypes, areaTypes, findTypeValueByKey } from "utils/types"
import {
  UPDATE_ANNOUNCEMENT,
  CREATE_ANNOUNCEMENT,
  GET_COMPANIES
} from "../../gql"

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview)

const AnnouncementForm = (props) => {
  const [localData, setLocalData] = useState(
    props.data
      ? props.data
      : {
          title: "",
          content: "",
          type: "",
          startTime: "",
          isNotificationAssistant: false,
          companies: []
        }
  )
  const [selectedCompanies, setSelectedCompanies] = useState(
    props.data
      ? props.data.companies.map((company) => {
          return {
            value: company.id
          }
        })
      : []
  )
  const { data: companyData } = useQuery(GET_COMPANIES)
  const [
    updateAnnouncement,
    { loading: updateLoading, error: updateError }
  ] = useMutation(UPDATE_ANNOUNCEMENT)
  const [
    createAnnouncement,
    { loading: createLoading, error: createError }
  ] = useMutation(CREATE_ANNOUNCEMENT)

  const checkRequire = () => {
    return (
      localData.title === "" ||
      localData.content === "" ||
      localData.type === "" ||
      localData.startTime === "" ||
      selectedCompanies.length === 0
    )
  }

  const getCompanyOptions = useMemo(() => {
    return companyData?.companies?.data
      .filter((item) => item.area !== "ALL")
      .map((item) => {
        return {
          value: item.id,
          label: `${item.name} - ${findTypeValueByKey(areaTypes, item.area)}`
        }
      })
  }, [companyData])

  const companyIds = useMemo(
    () => selectedCompanies.map((company) => company.value),
    [selectedCompanies]
  )

  const onFieldChange = (key, value) => {
    setLocalData({
      ...localData,
      [key]: value
    })
  }

  const variableFromData = () => {
    return {
      id: localData.id || undefined,
      input: {
        title: localData.title,
        content: localData.content,
        type: localData.type,
        startTime: localData.startTime,
        companies: selectedCompanies.map((company) => company.value),
        isNotificationAssistant: localData.isNotificationAssistant,
        file: localData.file || undefined
      }
    }
  }

  const onSaveButtonClick = async () => {
    if (localData.id) {
      await updateAnnouncement({
        variables: variableFromData()
      })
    } else {
      await createAnnouncement({
        variables: variableFromData()
      })
    }

    props.history.goBack()
  }

  const announcementTypeOptions = useMemo(() => {
    return announcementTypes.map((item) => {
      return (
        <option key={item.key} value={item.key}>
          {item.value}
        </option>
      )
    })
  }, [])

  return (
    <div className="">
      <Card>
        <CardHeader>
          <BackButton left />
        </CardHeader>
        <CardBody>
          <Form
            style={{
              marginLeft: "10px",
              marginRight: "10px"
            }}
          >
            <FormGroup row>
              <Label for="title">標題 *</Label>
              <Input
                type="text"
                name="title"
                id="title"
                placeholder="標題"
                value={localData.title}
                onChange={(e) => onFieldChange("title", e.target.value)}
              />
            </FormGroup>
            <FormGroup row>
              <Label for="content">內容 *</Label>
              <Input
                type="textarea"
                name="content"
                id="content"
                placeholder="內容"
                value={localData.content}
                onChange={(e) => onFieldChange("content", e.target.value)}
              />
            </FormGroup>
            <FormGroup row style={{ marginBottom: "0" }}>
              <Label for="startTime">發布時間 *</Label>
            </FormGroup>
            <div style={{ marginBottom: "10px", marginLeft: "-12px" }}>
              <Datetime
                dateFormat="YYYY-MM-DD"
                timeFormat="HH:mm"
                closeOnSelect={true}
                closeOnTab={true}
                value={
                  localData.startTime
                    ? moment(localData.startTime).format("YYYY-MM-DD HH:mm")
                    : ""
                }
                onChange={(date) => {
                  onFieldChange("startTime", date.format("YYYY-MM-DD HH:mm"))
                }}
                inputProps={{ placeholder: "選擇公告發布時間", readOnly: true }}
              />
            </div>
            <FormGroup row>
              <Label for="type">類型 *</Label>
              <Input
                type="select"
                name="type"
                id="type"
                value={localData.type}
                onChange={(e) => onFieldChange("type", e.target.value)}
              >
                <option value="">請選擇公告類型</option>
                {announcementTypeOptions}
              </Input>
            </FormGroup>
            <FormGroup row>
              <Label>選擇公司 *</Label>
              <Button
                className="ml-2"
                onClick={() => {
                  setSelectedCompanies(getCompanyOptions)
                }}
              >
                全選
              </Button>
            </FormGroup>
            <FormGroup row>
              <div style={{ width: "100%" }}>
                <Select
                  placeholder="選擇公司"
                  isMulti={true}
                  value={_.filter(getCompanyOptions, (option) =>
                    companyIds.includes(option.value)
                  )}
                  options={getCompanyOptions}
                  onChange={(option) => {
                    option
                      ? setSelectedCompanies(option)
                      : setSelectedCompanies([])
                  }}
                />
              </div>
            </FormGroup>

            <FormGroup row style={{ marginBottom: "0" }}>
              <Label>圖片</Label>
            </FormGroup>
            <FormGroup row>
              {localData.photoUrl && (
                <Img
                  style={{ maxWidth: "100%" }}
                  src={`http://${localData.photoUrl}`}
                  name="公告圖片"
                />
              )}
            </FormGroup>
            <FilePond
              labelIdle='將檔案拖拉至此 或 <span class="filepond--label-action">瀏覽檔案</span>'
              allowMultiple={false}
              beforeAddFile={(item) => {
                return new Promise((resolve) => {
                  if (
                    item.file.type === "image/jpg" ||
                    item.file.type === "image/jpeg" ||
                    item.file.type === "image/png"
                  ) {
                    onFieldChange("file", item.file)
                    return resolve(true)
                  }
                  props.notify.warn("請上傳 jpeg, jpg, png 格式")
                  return resolve(false)
                })
              }}
              beforeRemoveFile={() => {
                onFieldChange("file", null)
              }}
            />
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={localData.isNotificationAssistant}
                  onChange={(e) =>
                    onFieldChange("isNotificationAssistant", e.target.checked)
                  }
                />{" "}
                是否通知業務助理
              </Label>
            </FormGroup>
          </Form>
        </CardBody>
        <CardFooter>
          <Button
            color="primary"
            disabled={checkRequire()}
            onClick={onSaveButtonClick}
          >
            送出
          </Button>
        </CardFooter>
        <BarLoader loading={updateLoading || createLoading} />
      </Card>
      {(createError || updateError) && (
        <Alert color="danger">
          錯誤訊息：{`${createError || updateError}`}
        </Alert>
      )}
    </div>
  )
}

export default compose(withBasicNotify, withRouter)(AnnouncementForm)
